/**
 * Post-purchase phone verification — the pure decisions.
 *
 * Contract shared with iOS and Android: `docs/claude/post-purchase-phone-verification.md`. Everything
 * here is a plain function over plain values so the three surfaces can be checked against each other,
 * and so these rules are testable without a router, a query client or a clock.
 */

/** How long the screen looks like a hard block before the way out appears. */
export const SKIP_REVEAL_SECONDS = 60;

/**
 * localStorage key for the latch, per user — a different account must not inherit the ask.
 *
 * Deliberately NOT the `orion-web:` prefix: that prefix is kept only by keys that already exist in
 * live browsers, where renaming would reset what they remember. A new key has no such cost and is
 * named for the product (see orion-web/CLAUDE.md).
 */
export function pendingAskKey(userId: string): string {
  return `symphony-post-purchase-phone-ask:${userId}`;
}

/** localStorage key for when the current gate was first shown, per user. */
export function askStartedAtKey(userId: string): string {
  return `symphony-post-purchase-phone-started-at:${userId}`;
}

/**
 * Whether this person should be asked for a number.
 *
 * `hasPhone` wins over everything. Somebody who already gave us a number is NEVER asked again — not
 * because we can prove they answered a code for it (we often cannot; `FILL_IF_ABSENT` writes
 * caller-supplied numbers), but because asking a paying customer to re-confirm a number they already
 * handed over is a worse outcome than holding a number we cannot prove. Product decision, 2026-09-21.
 *
 * `becamePremium` is the TRANSITION, never the state. "Is premium and has no phone" would sweep in the
 * entire existing paid base, who were promised nothing of the kind.
 *
 * `phoneKnown` is the third state that makes this safe: while the recipient read is in flight we do not
 * know whether there is a phone, and treating unknown as "none" puts the ask in front of someone who
 * already gave one — the same trap `useNeedsPhoneConnect` documents.
 */
export function shouldAskForPhone(input: {
  becamePremium: boolean;
  hasPhone: boolean;
  phoneKnown: boolean;
}): boolean {
  if (!input.phoneKnown) return false;
  if (input.hasPhone) return false;
  return input.becamePremium;
}

/**
 * Whether the Skip control is visible yet.
 *
 * FAILS TOWARD SHOWING. `elapsedSeconds` is null whenever we cannot say how long the screen has been
 * up — no stored start instant, a cleared or unreadable store, a restored tab. In every one of those
 * cases the button appears. The cost of showing it early is one nag skipped; the cost of the opposite
 * is a paying customer held on a screen with no way out because a timer was lost, and that must not be
 * reachable by any bug in this file.
 */
export function skipVisible(
  elapsedSeconds: number | null,
  revealAfterSeconds: number = SKIP_REVEAL_SECONDS,
): boolean {
  if (elapsedSeconds === null || !Number.isFinite(elapsedSeconds)) return true;
  return elapsedSeconds >= revealAfterSeconds;
}

/**
 * Seconds the gate has been up, from a stored START INSTANT rather than accumulated ticks.
 *
 * A counter that ticks while mounted restarts on every remount — a tab switch, a re-render, a restored
 * process — so the 60 seconds would never elapse for anyone whose session is interrupted. Measuring
 * from a fixed instant makes the wait real time, and makes it survive everything that unmounts a
 * React tree.
 *
 * A start instant in the FUTURE (a clock that moved backwards, a value we cannot trust) reads as
 * unknown rather than as a very long wait, so it lands on the fail-open branch above.
 */
export function elapsedSecondsSince(startedAtMs: number | null, nowMs: number): number | null {
  if (startedAtMs === null || !Number.isFinite(startedAtMs)) return null;
  const delta = (nowMs - startedAtMs) / 1000;
  if (delta < 0) return null;
  return delta;
}

/**
 * The Skip decision as the GATE actually faces it, rather than as `skipVisible` faces it in the
 * abstract. This exists because the first version got the very first render wrong in a way no unit
 * test could see: the start instant was stamped in an effect, so the first paint read `null`, called
 * that "unknown", and revealed Skip immediately — the 60-second block never happened at all. Caught by
 * opening the page, which is the argument for opening the page.
 *
 * Two different situations produce a missing instant, and they must not share an answer:
 *
 * - **The gate just opened.** There is nothing stored yet because no time has passed. Elapsed is ZERO,
 *   not unknown, and the Skip stays hidden. Resolving this during render is what makes the block real.
 * - **We cannot persist the instant at all** (storage blocked or throwing). Then the wait cannot be
 *   bounded: every remount would stamp a fresh "now" and restart the minute, which is the unbounded
 *   wait the stored-instant rule exists to prevent. So this one fails toward SHOWING — an unbounded
 *   block on somebody who has paid is worse than a nag they can dismiss a minute early.
 */
export function gateSkipVisible(input: {
  storedStartedAtMs: number | null;
  couldPersist: boolean;
  nowMs: number;
  revealAfterSeconds?: number;
}): boolean {
  if (input.storedStartedAtMs === null && !input.couldPersist) return true;
  const start = input.storedStartedAtMs ?? input.nowMs;
  return skipVisible(elapsedSecondsSince(start, input.nowMs), input.revealAfterSeconds);
}

/**
 * Whether the purchase latch should be raised now.
 *
 * Separate from `shouldAskForPhone` because they answer different questions at different moments: this
 * one fires once, on the edge; the other decides, on every render, whether the screen is up. The latch
 * is what makes closing the tab the instant a purchase lands still produce the ask at the next open,
 * instead of never.
 */
export function shouldRaiseLatch(input: {
  wasPremium: boolean | null;
  isPremium: boolean;
  hasPhone: boolean;
  phoneKnown: boolean;
}): boolean {
  // A null previous value is the FIRST observation of this session, not a transition. Treating it as
  // "was free" would raise the latch for every existing premium user on their next page load, which is
  // exactly the sweep the transition rule exists to avoid.
  if (input.wasPremium === null) return false;
  if (!input.phoneKnown || input.hasPhone) return false;
  return !input.wasPremium && input.isPremium;
}

/**
 * Whether the latch should be dropped.
 *
 * A phone arriving by ANY route clears it — the gate itself, a settings screen, another device, an
 * agent saving a number the person typed in chat. Two surfaces must not both ask, and a person who has
 * just given us a number must not see the request again on the next open.
 */
export function shouldClearLatch(input: { hasPhone: boolean; phoneKnown: boolean }): boolean {
  return input.phoneKnown && input.hasPhone;
}
